"use client";

import { useState, type Ref } from "react";
import { cn } from "@/lib/utils";
import type { StaffMember } from "./mock-data";

/**
 * A textarea that offers colleagues to @mention as you type.
 *
 * Typing "@" followed by part of a name opens a list; picking someone writes
 * "@Full Name " into the text. Who a note mentions is read back from the text
 * (mentionedIn), so deleting the name un-mentions them - there is no separate
 * list to keep in step.
 */
export function MentionTextarea({
  value,
  onChange,
  staff,
  textareaRef,
  placeholder,
  className,
  rows = 4,
}: {
  value: string;
  onChange: (value: string) => void;
  /** People who can be mentioned. */
  staff: StaffMember[];
  textareaRef?: Ref<HTMLTextAreaElement>;
  placeholder?: string;
  className?: string;
  rows?: number;
}) {
  // The "@query" being typed at the caret, if any.
  const [query, setQuery] = useState<{ text: string; start: number } | null>(
    null,
  );
  const [active, setActive] = useState(0);

  // Everyone who matches; the list scrolls when there are many.
  const matches = query
    ? staff.filter((s) =>
        s.name.toLowerCase().includes(query.text.toLowerCase()),
      )
    : [];

  const detect = (text: string, caret: number) => {
    const found = /(^|\s)@([^\s@]*)$/.exec(text.slice(0, caret));
    setQuery(
      found ? { text: found[2], start: caret - found[2].length - 1 } : null,
    );
    setActive(0);
  };

  const pick = (person: StaffMember, el: HTMLTextAreaElement) => {
    if (!query) return;
    const end = query.start + 1 + query.text.length;
    const inserted = `@${person.name} `;
    const next = value.slice(0, query.start) + inserted + value.slice(end);
    onChange(next);
    setQuery(null);

    const caret = query.start + inserted.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        autoFocus
        rows={rows}
        value={value}
        placeholder={placeholder}
        className={className}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onClick={(e) =>
          detect(e.currentTarget.value, e.currentTarget.selectionStart)
        }
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        onKeyDown={(e) => {
          if (!matches.length) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const step = e.key === "ArrowDown" ? 1 : -1;
            setActive((i) => (i + step + matches.length) % matches.length);
          } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            pick(matches[active], e.currentTarget);
          } else if (e.key === "Escape") {
            setQuery(null);
          }
        }}
      />
      {matches.length > 0 && (
        <div className="absolute bottom-full left-4 z-20 mb-1 max-h-64 w-60 overflow-y-auto rounded border border-gray-200 bg-white p-1 shadow-md">
          {matches.map((person, i) => (
            <button
              key={person.id}
              type="button"
              // Arrow keys move the highlight; keep it in view.
              ref={
                i === active
                  ? (el) => el?.scrollIntoView({ block: "nearest" })
                  : undefined
              }
              // Before the textarea's blur, so the pick still lands.
              onMouseDown={(e) => {
                e.preventDefault();
                const el = e.currentTarget
                  .closest("div.relative")
                  ?.querySelector("textarea");
                if (el) pick(person, el);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs",
                i === active ? "bg-gray-100" : "hover:bg-gray-50",
              )}
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[9px] font-medium text-gray-700">
                {person.initials}
              </span>
              <span className="flex-1 truncate">{person.name}</span>
              <span className="text-[10px] text-gray-400">{person.days}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Everyone whose "@Full Name" appears in the text. */
export function mentionedIn(text: string, staff: StaffMember[]): string[] {
  return staff
    .filter((person) => text.includes(`@${person.name}`))
    .map((person) => person.id);
}

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Renders a note with "@Full Name" of known staff highlighted. */
export function withMentions(text: string, staff: StaffMember[]) {
  const names = staff
    .map((person) => person.name)
    .filter(Boolean)
    // Longest first, so "Anna Lind" wins over "Anna".
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  if (!names.length) return text;

  const parts = text.split(new RegExp(`(@(?:${names.join("|")}))`, "g"));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span
        key={i}
        className="rounded bg-sky-100 px-0.5 font-medium text-sky-800"
      >
        {part}
      </span>
    ) : (
      part
    ),
  );
}
